/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Plus, Layers, Edit2, Trash2, TrendingUp, TrendingDown, Coins, Wallet, Percent, Award, Boxes, Save, X, Loader2, Check, Download, ChevronDown, ArrowUp, ArrowDown, Tag, ArrowLeft } from "lucide-react";
import { resolveBackHref } from "@/lib/navTracker";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useTier } from "@/context/AuthContext";
import EmptyState from "@/components/EmptyState";
import CategoryPill from "@/components/CategoryPill";
import BottomNav from "@/components/BottomNav";
import SectionHeader from "@/components/SectionHeader";
import WineCategoryGuidedModal from "@/components/WineCategoryGuidedModal";
import {
  ChartFrame, ChartEmpty, ValueLineChart, ProfitLineChart,
  CategoryPieChart, CategoryBarChart,
} from "@/components/charts/Charts";
import { itemCost, itemValue, itemProfit, itemQty, isValueInferred, computeItemValuation } from "@/utils/itemValue";
import InferredValueIndicator from "@/components/InferredValueIndicator";
import { fmt2 } from "@/lib/money";
import { composeItemDescription } from "@/lib/itemDescription";
import SalesHistoryTable from "@/components/SalesHistoryTable";
import { summariseSales } from "@/utils/salesTotals";

// IS_PREVIEW — true when running in the sandboxed Preview environment
// where browser downloads do NOT actually persist a file. Used ONLY to
// swap user-facing CSV messaging.
const IS_PREVIEW = typeof window !== "undefined" && /\bpreview\b/.test(window.location.hostname);

/* Small uppercase pill — matches the badge used in Items.jsx and ItemDetail.jsx. */
const CurrencyBadge = ({ code, testid }) => (
  <span
    data-testid={testid}
    className="ml-1.5 text-[9px] font-sans font-medium tracking-wider uppercase px-1.5 py-0.5 rounded border border-[hsl(var(--border))] vp-muted align-baseline"
  >
    {code || "NZD"}
  </span>
);
const itemDateMs = (it) => {
  const d = it.date_added || it.created_at || it.purchase_date;
  if (!d) return 0;
  const t = Date.parse(d);
  return isNaN(t) ? 0 : t;
};

export default function CollectionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [collection, setCollection] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  // Wine category guided modal — opened by the new "Create Category" toolbar
  // button AND auto-opened when the user lands here right after creating a
  // Wine collection (?just_created=1 query flag set by Collections.jsx).
  const [showWineCatModal, setShowWineCatModal] = useState(false);
  // Inline-confirm flow — replaces window.confirm() (silently blocked in webviews).
  const [confirmDeleteColl, setConfirmDeleteColl] = useState(false);
  const [deletingColl, setDeletingColl] = useState(false);
  const { canCsvExport, isAdmin } = useTier();
  // Second-step modal: shown AFTER the CSV backup has been kicked off, BEFORE
  // the destructive DELETE call. Holds the suggested backup filename so the
  // user can see exactly which file just hit their downloads folder.
  const [deleteAfterBackup, setDeleteAfterBackup] = useState(null); // { filename } | null
  const [confirmDeleteItemId, setConfirmDeleteItemId] = useState(null);
  const [deletingItemId, setDeletingItemId] = useState(null);

  // Defensive refetch: bumps on every navigation event (location.key) so a
  // return trip from ItemDetail/ItemEntry always pulls fresh totals, and on
  // tab focus so a user who edits in another tab / backgrounds the page
  // still sees the latest numbers. `_t=` cache-busts any intermediate
  // proxy that might have ignored the no-store header.
  const [refreshedAt, setRefreshedAt] = useState(0);
  useEffect(() => {
    const bump = () => {
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        setRefreshedAt((n) => n + 1);
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", bump);
    }
    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", bump);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const t = Date.now();
        const [cRes, iRes, catRes, sRes] = await Promise.all([
          apiClient.get(`/collections/${id}?_t=${t}`),
          apiClient.get(`/items?collection_id=${id}&_t=${t}`),
          apiClient.get(`/categories?_t=${t}`),
          apiClient.get(`/sales?collection_id=${id}&_t=${t}`),
        ]);
        if (cancelled) return;
        setCollection(cRes.data);
        setItems(iRes.data || []);
        setCategories(catRes.data || []);
        setSales(sRes.data || []);
        // Persist the last-opened collection so the Home screen can offer a
        // "Continue: {name}" pill for quick return next session. Failures
        // (private mode / storage disabled) are silently tolerated. We also
        // clear `cdi.lastItemId` here: the user has moved up to the
        // collection level, so the Item-level resume pill is no longer the
        // right resume target.
        try {
          localStorage.setItem("cdi.lastCollectionId", id);
          localStorage.removeItem("cdi.lastItemId");
        } catch (_e) { /* noop */ }
      } catch {
        if (!cancelled) toast.error("Failed to load collection.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, location.key, refreshedAt]);

  // Refreshes only the categories list (used by the guided modal so the new
  // category appears immediately without forcing a full collection reload).
  const refreshCategories = async () => {
    try {
      const { data } = await apiClient.get("/categories");
      setCategories(data || []);
    } catch {
      /* tolerated — the user can also refresh the page */
    }
  };

  // Auto-open the guided modal on `?just_created=1` IFF the collection's
  // name suggests it's a Wine collection (case-insensitive substring match).
  // The flag is consumed (stripped from the URL) on first read so a manual
  // refresh of the Collection screen doesn't keep popping the modal.
  useEffect(() => {
    if (!collection) return;
    if (searchParams.get("just_created") !== "1") return;
    const looksLikeWine = /wine/i.test(collection.name || "")
      || /wine/i.test(collection.description || "");
    if (looksLikeWine) setShowWineCatModal(true);
    // Strip the flag regardless so re-renders don't re-open the modal.
    const next = new URLSearchParams(searchParams);
    next.delete("just_created");
    setSearchParams(next, { replace: true });
  }, [collection, searchParams, setSearchParams]);

  const catObj = (cid) => categories.find((c) => c.id === cid) || null;
  const symbol = collection?.currency_symbol || "$";
  const money = (n) => `${symbol}${fmt2(n)}`;

  // Sortable column header state. Default: by Name ascending (most natural for browsing).
  const [sortKey, setSortKey] = useState("name");   // name | cost | value | profit
  const [sortDir, setSortDir] = useState("asc");    // asc | desc
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "name" ? "asc" : "desc"); }
  };
  const sortedItems = useMemo(() => {
    const labelOf = (it) =>
      ((it.item_data && it.item_data["Item Name"]) || it.name || it.item_name || "").toLowerCase();
    const keyFn = {
      name:   labelOf,
      cost:   itemCost,
      value:  itemValue,
      profit: itemProfit,
    }[sortKey];
    const arr = [...items].sort((a, b) => {
      const va = keyFn(a), vb = keyFn(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [items, sortKey, sortDir]);

  /* === Analytics === */
  // Precompute per-item valuation using the industry-standard formula
  // (2026-02 — mirrors backend `_compute_item_valuation`).
  const salesByItem = useMemo(() => {
    const map = new Map();
    for (const s of sales) {
      const k = s.item_id;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(s);
    }
    return map;
  }, [sales]);
  const valuations = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      map.set(it.id, computeItemValuation(it, salesByItem.get(it.id) || []));
    }
    return map;
  }, [items, salesByItem]);
  const _val = (it) => valuations.get(it?.id) || { current_value: 0, total_cost: 0, total_PL: 0, realised_PL: 0, unrealised_PL: 0, includeInValuationTotals: false };

  // KPI totals (2026-02-26) — mirrors Analytics.jsx standard.
  // Cost is ALWAYS summed (an unvalued item still represents money spent).
  // Value / P/L / valuedCount are gated on `includeInValuationTotals` so
  // items with no Manual / Market / Sale valuation never poison the
  // Collection Value or Gain/Loss tiles with a fabricated 0.
  const kpi = useMemo(() => {
    let totalCost = 0;
    let totalValue = 0;
    let totalPL = 0;
    let valuedCount = 0;
    let totalBottles = 0;
    for (const it of items) {
      const v = _val(it);
      totalCost += v.total_cost;
      totalBottles += itemQty(it);
      if (v.includeInValuationTotals) {
        totalValue += v.current_value;
        totalPL += v.total_PL;
        valuedCount += 1;
      }
    }
    const pl = totalPL;
    const pct = totalCost > 0 ? (totalPL / totalCost) * 100 : 0;
    const avgValue = totalBottles > 0 ? totalValue / totalBottles : 0;
    return { count: items.length, totalCost, totalValue, pl, pct, avgValue, totalBottles, valuedCount };
  }, [items, valuations]);

  // Realised-side rollup for this collection.
  const salesSummary = useMemo(() => summariseSales(sales), [sales]);
  const combinedPerformance = kpi.pl + salesSummary.realisedEx;

  /**
   * Generic item_data[<name>] rollup — for Region / Producer / Grape / wine.Category.
   * Skips empty values, flattens list-valued fields (e.g. Grape(s) saved as array).
   * Returns rows pre-sorted by total_est_value (high to low).
   * All monetary values use the industry-standard valuation formula so
   * heterogeneous-currency collections aggregate correctly and match
   * the KPI grid above.
   */
  const byItemDataField = (name) => {
    const map = {};
    for (const it of items) {
      const raw = (it.item_data || {})[name];
      const labels = Array.isArray(raw)
        ? raw.map((x) => String(x).trim()).filter(Boolean)
        : (raw === undefined || raw === null || raw === "" ? [] : [String(raw).trim()]);
      if (labels.length === 0) continue;
      const v = _val(it);
      const c = v.total_cost;
      const val = v.current_value;
      const p = v.total_PL;
      for (const label of labels) {
        if (!map[label]) {
          map[label] = { name: label, total_cost: 0, total_est_value: 0, total_profit: 0, count: 0 };
        }
        // Cost is unconditional — sum for every item regardless of
        // valuation state (matches Analytics.jsx standard).
        map[label].total_cost += c;
        // Value + profit only accumulate when the item carries a
        // Manual / Market / Most Recent Sale valuation. Unvalued
        // items contribute $0 here to avoid polluting the breakdown
        // rows with fabricated value.
        if (v.includeInValuationTotals) {
          map[label].total_est_value += val;
          map[label].total_profit += p;
        }
        map[label].count += 1;
      }
    }
    return Object.values(map).sort((a, b) => b.total_est_value - a.total_est_value);
  };

  // The four wine breakdowns the user asked for.
  const byRegion   = useMemo(() => byItemDataField("Region"),   [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const byProducer = useMemo(() => byItemDataField("Producer"), [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const byGrape    = useMemo(() => byItemDataField("Grape(s)"), [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const byWineCategory = useMemo(() => byItemDataField("Category"), [items]); // eslint-disable-line react-hooks/exhaustive-deps

  // Spirits-specific breakdowns — isolated from wine dimensions so the
  // Spirits collection page never renders Wine analytics rollups.
  // Keys mirror the exact item_data labels used by Spirits ItemEntry
  // (Producer / Distillery, Type, Cask Type, Country).
  const isSpiritsCol = (collection?.type_id || "").toLowerCase() === "spirits";
  const isWineCol = (collection?.type_id || "").toLowerCase() === "wine";
  const byDistillery = useMemo(() => byItemDataField("Producer / Distillery"), [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const bySpiritType = useMemo(() => byItemDataField("Type"),                  [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const byCaskType   = useMemo(() => byItemDataField("Cask Type"),             [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const byCountry    = useMemo(() => byItemDataField("Country"),               [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const byCategory = useMemo(() => {
    const map = {};
    for (const it of items) {
      const k = it.category_id || "__none";
      if (!map[k]) {
        map[k] = {
          name: catObj(it.category_id)?.category_name || "Uncategorised",
          // Rollups — the tooltip reads these directly so every chart that
          // uses this dataset shows the same numbers regardless of which
          // bar is hovered.
          total_cost: 0,
          total_est_value: 0,
          total_profit: 0,
          count: 0,
          // Back-compat aliases used by some existing chart bars.
          cost: 0,
          value: 0,
          profit: 0,
        };
      }
      const val = _val(it);
      const c = val.total_cost;
      const v = val.current_value;
      const p = val.total_PL;
      // Cost is unconditional. Value / profit and their back-compat
      // aliases only accumulate for items with a real valuation so
      // unvalued items don't inflate the category rollup / charts.
      map[k].total_cost += c;
      map[k].cost = map[k].total_cost;
      if (val.includeInValuationTotals) {
        map[k].total_est_value += v;
        map[k].total_profit += p;
        map[k].value = map[k].total_est_value;
        map[k].profit = map[k].total_profit;
      }
      map[k].count += 1;
    }
    return Object.values(map).sort((a, b) => b.total_est_value - a.total_est_value);
  }, [items, categories, valuations]);

  const valuePieData = useMemo(
    () => byCategory.filter((r) => r.total_est_value > 0).map((r) => ({ name: r.name, value: r.total_est_value })),
    [byCategory]
  );
  // Cost-vs-Value chart: two bars (Cost + Est. Value) per category. Each row
  // still carries the full rollup so the tooltip can render Cost/Value/Profit.
  const costValueBarData = useMemo(
    () => byCategory.map((r) => ({
      name: r.name,
      cost: r.total_cost,
      estimated: r.total_est_value,
      total_cost: r.total_cost,
      total_est_value: r.total_est_value,
      total_profit: r.total_profit,
    })),
    [byCategory]
  );
  // Profit chart: single bar driven by total_profit; row carries full rollup.
  const profitBarData = useMemo(
    () => byCategory.map((r) => ({
      name: r.name,
      estimated: r.total_profit,           // back-compat key used by the bar
      total_cost: r.total_cost,
      total_est_value: r.total_est_value,
      total_profit: r.total_profit,
    })),
    [byCategory]
  );

  /* Time-series: bucket by month using date_added, accumulate value & profit.
     Uses _val(it) (industry-standard valuation) rather than the legacy
     itemValue/itemProfit helpers so unvalued items contribute 0 to the
     cumulative series and never cause negative dips in the charts. */
  const timeSeries = useMemo(() => {
    if (items.length === 0) return [];
    const sorted = [...items].sort((a, b) => itemDateMs(a) - itemDateMs(b));
    const buckets = new Map(); // monthKey → { date, cumValue, cumProfit }
    let cv = 0;
    let cp = 0;
    for (const it of sorted) {
      const v = _val(it);
      if (v.includeInValuationTotals) {
        cv += v.current_value;
        cp += v.total_PL;
      }
      const ms = itemDateMs(it);
      const d = ms ? new Date(ms) : new Date();
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
      buckets.set(key, { date: label, value: Math.round(cv * 100) / 100, profit: Math.round(cp * 100) / 100 });
    }
    return Array.from(buckets.values());
  }, [items, valuations]);

  /* Item insights */
  const insights = useMemo(() => {
    // Highest Value + Average Value read from the current inventory but
    // consider ONLY items that carry a real valuation (Manual / Market /
    // Most Recent Sale). Unvalued items would otherwise skew "highest"
    // to $0 and drag the average down toward zero.
    const valuedItems = items.filter((it) => _val(it).includeInValuationTotals);
    const hasValued = valuedItems.length > 0;
    const highestValue = hasValued
      ? valuedItems.slice().sort((a, b) => _val(b).current_value - _val(a).current_value)[0]
      : null;
    const avgValue = hasValued
      ? valuedItems.reduce((s, it) => s + _val(it).current_value, 0) / valuedItems.length
      : 0;

    // Highest Profit + Biggest Loss (Phase 10, 2026-02) now read from
    // the collection-level sales ledger so realised P/L reflects
    // EVERY historical sale, including sales tied to items that have
    // since been deleted (orphan / ghost rows from Phase 9). The
    // ledger already snapshots `item_name`, `producer_or_brand`, and
    // realised profit at sale time, so orphan sales still render with
    // a meaningful label. Multi-line sales on the same item are
    // ranked per-row per the spec ("returns the sale with the
    // highest realised profit"). Inventory-based analytics
    // (categoryAggregators, dashboardAggregators, computeItemValuation)
    // are NOT called from this branch — they stay inventory-only.
    const aliveIds = new Set(items.map((it) => it.id));
    const rankedSales = (sales || [])
      .filter((s) => typeof s.realised_profit_ex_gst === "number")
      .slice()
      .sort(
        (a, b) =>
          Number(b.realised_profit_ex_gst || 0) -
          Number(a.realised_profit_ex_gst || 0)
      );
    const saleToInsight = (s) => {
      if (!s) return { insightItem: null, display: 0 };
      const realised = Number(s.realised_profit_ex_gst || 0);
      const isOrphan = !aliveIds.has(s.item_id);
      const label =
        (s.item_name && s.item_name.trim()) ||
        (s.producer_or_brand && s.producer_or_brand.trim()) ||
        "Untitled";
      return {
        insightItem: {
          id: s.item_id,
          name: label,
          orphan: isOrphan,
        },
        display: realised,
      };
    };
    const top = saleToInsight(rankedSales[0]);
    const bot = saleToInsight(rankedSales[rankedSales.length - 1]);

    // Guard: if there are NO items AND NO sales, return null to
    // suppress the panel entirely (mirrors pre-Phase-10 behaviour
    // for empty collections).
    if (!hasValued && rankedSales.length === 0) return null;

    return {
      highestValue,
      avgValue,
      // New ledger-driven fields
      highestProfitItem: top.insightItem,
      highestProfitDisplay: top.display,
      biggestLossItem: bot.insightItem,
      biggestLossDisplay: bot.display,
    };
  }, [items, sales, valuations]);

  const startEdit = () => {
    setEditName(collection?.name || "");
    setEditDescription(collection?.description || "");
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditName("");
    setEditDescription("");
  };

  const saveEdit = async () => {
    if (!editName.trim()) { toast.error("Name is required."); return; }
    setSavingEdit(true);
    try {
      const { data } = await apiClient.patch(`/collections/${id}`, {
        name: editName.trim(),
        description: editDescription.trim(),
      });
      setCollection(data);
      toast.success("Collection updated.");
      setEditing(false);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = Array.isArray(raw)
        ? raw.map((er) => `${(er.loc || []).slice(-1)[0] || "field"}: ${er.msg}`).join("; ")
        : (typeof raw === "string" ? raw : "Failed to save.");
      toast.error(msg);
    } finally {
      setSavingEdit(false);
    }
  };

  // Inline armed "Delete" click — just opens the confirmation modal. No
  // CSV, no DELETE yet. CSV (Build only) + DELETE both happen only when
  // the user confirms inside the modal. Mirrors the unified handler in
  // Collections.jsx so both delete entry points behave identically.
  const deleteCollection = () => {
    if (deletingColl) return;
    setDeleteAfterBackup({ id });
  };

  // Called from the modal "Confirm Delete" / "Delete without backup"
  // button — Build mode runs CSV backup FIRST (fail-closed), Preview mode
  // skips CSV entirely. Then performs the actual DELETE and navigates
  // back to /collections.
  const finalDeleteCollection = async () => {
    if (deletingColl) return;
    setDeletingColl(true);
    // Step 1 — CSV backup BEFORE destructive call. Build mode only,
    // and only when the user's plan permits CSV export.
    if (!IS_PREVIEW && canCsvExport) {
      try {
        exportCollectionCsv({ filenamePrefix: "cdi-backup" });
      } catch {
        // Backup helper already toasted the failure; abort deletion.
        setDeletingColl(false);
        return;
      }
      // Give the browser a beat to start writing the file before we fire
      // the destructive request.
      await new Promise((r) => setTimeout(r, 400));
    }
    // Step 2 — actual delete.
    try {
      await apiClient.delete(`/collections/${id}`);
      toast.success("Collection deleted.");
      navigate("/collections");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
      setDeletingColl(false);
      setDeleteAfterBackup(null);
      setConfirmDeleteColl(false);
    }
  };

  const cancelFinalDelete = () => {
    setDeleteAfterBackup(null);
    setConfirmDeleteColl(false);
    toast("Delete cancelled.", { icon: "🛡️" });
  };

  const deleteItem = async (itemId) => {
    if (deletingItemId) return;
    setDeletingItemId(itemId);
    try {
      await apiClient.delete(`/items/${itemId}`);
      toast.success("Item deleted.");
      setConfirmDeleteItemId(null);
      const { data } = await apiClient.get(`/items?collection_id=${id}`);
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setDeletingItemId(null);
    }
  };

  // ---- Export Collection (CSV) ---------------------------------------
  // Client-side blob download. Mirrors the schema used by the Items list
  // exporter so the two CSVs are reconcilable.
  const csvEscape = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const catName = (cid) => categories.find((c) => c.id === cid)?.category_name || "";
  const exportCollectionCsv = ({ filenamePrefix = "cdi" } = {}) => {
    // Preview-mode gate applies ONLY to the delete-time backup path.
    // Export CSV ("cdi" / "cdi-export") is uncoupled from delete safety
    // rules and must always succeed — including in Preview. The backup
    // path stays fail-closed so a delete never proceeds without a real CSV.
    if (IS_PREVIEW && filenamePrefix === "cdi-backup") {
      toast.error("CSV backup is disabled in Preview mode.");
      throw new Error("CSV backup disabled in Preview mode");
    }
    if (!collection || items.length === 0) {
      // Allow zero-item backups to still write a header-only file so the user
      // gets clear feedback that the delete safety step executed.
      if (filenamePrefix !== "cdi-backup") { toast.error("Nothing to export."); return; }
    }
    const cols = ["name","collection","category","currency","cost","est_value","profit","created_at"];
    const lines = [cols.join(",")];
    // Created At resolver — items may store the timestamp under several
    // keys depending on the creation path: the API currently serialises
    // it as `date_added`, while legacy / camelCase variants are also
    // possible. Pick the first non-null and format as
    // "YYYY-MM-DD HH:mm:ss" (UTC) so the column is always populated.
    const fmtCreatedAt = (it) => {
      const raw = it.date_added ?? it.created_at ?? it.createdAtUtc ?? it.createdAt;
      if (!raw) return "";
      const d = new Date(raw);
      if (isNaN(d.getTime())) return String(raw);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth()+1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
    };
    for (const it of items) {
      const cost = itemCost(it);
      const inferred = isValueInferred(it);
      const value = inferred ? null : itemValue(it);
      const row = {
        name: (it.item_data && it.item_data["Item Name"]) || it.name || it.item_name || "Untitled",
        collection: collection.name,
        category: catName(it.category_id),
        currency: it.currency_code || "NZD",
        cost: cost.toFixed(2),
        est_value: value === null ? "Awaiting Valuation" : value.toFixed(2),
        profit: value === null ? "Awaiting Valuation" : (value - cost).toFixed(2),
        created_at: fmtCreatedAt(it),
      };
      lines.push(cols.map((c) => csvEscape(row[c])).join(","));
    }
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date().toISOString().slice(0,10);
    const safe = (collection?.name || "collection").replace(/[^a-z0-9-_]+/gi, "-").toLowerCase();
    a.href = url;
    a.download = `${filenamePrefix}-${safe}-${today}.csv`;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    toast.success(`Exported ${items.length} row${items.length === 1 ? "" : "s"} → ${a.download}`);
  };

  // Context-aware Back: same resolver as ItemDetail (state.from → nav
  // tracker → fallback "/collections"). Mounted only when the page has
  // actually loaded a collection so it doesn't flicker during the skeleton.
  const backHref = resolveBackHref({
    stateFrom: location.state?.from,
    currentPath: location.pathname + location.search,
    fallback: "/collections",
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="collection-detail">
      <button
        type="button"
        onClick={() => navigate("/collections", { replace: true })}
        data-testid="cd-back"
        aria-label="Back to previous screen"
        className="vp-btn vp-btn-secondary vp-btn-sm mb-4 inline-flex items-center gap-2"
      >
        <ArrowLeft size={14} />
        <span>Back</span>
      </button>
      {deleteAfterBackup && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cd-modal-title"
          data-testid="cd-delete-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deletingColl) cancelFinalDelete();
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape" && !deletingColl) cancelFinalDelete();
          }}
          tabIndex={-1}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm vp-fade-in p-4"
        >
          <div
            className="vp-card max-w-md w-full p-6 shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={cancelFinalDelete}
              disabled={deletingColl}
              aria-label="Close"
              data-testid="cd-modal-close"
              className="absolute top-3 right-3 p-1 rounded hover:bg-[hsl(var(--muted))] vp-muted disabled:opacity-40"
            >
              <X size={16} />
            </button>
            <h2 id="cd-modal-title" className="text-base font-semibold mb-2">
              Delete collection?
            </h2>
            <p className="text-sm vp-muted mb-5">
              {!canCsvExport ? (
                <>CSV backup is disabled for your plan. You can still delete <span className="font-medium text-[hsl(var(--fg))]">{collection?.name}</span>, but no backup will be created.</>
              ) : IS_PREVIEW ? (
                <>CSV backup is disabled in Preview mode. You can still delete <span className="font-medium text-[hsl(var(--fg))]">{collection?.name}</span>, but no backup will be created.</>
              ) : (
                <>Deleting <span className="font-medium text-[hsl(var(--fg))]">{collection?.name}</span> will also remove every item and category inside it. This action cannot be undone from the UI. A CSV backup will be downloaded automatically before the delete runs.</>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelFinalDelete}
                disabled={deletingColl}
                data-testid="cd-modal-cancel"
                className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={finalDeleteCollection}
                disabled={deletingColl}
                data-testid="cd-modal-confirm"
                className="vp-btn vp-btn-sm bg-[hsl(var(--loss))] text-white hover:opacity-90 disabled:opacity-50"
              >
                {deletingColl ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                <span className="text-xs font-medium">{IS_PREVIEW || !canCsvExport ? "Delete without backup" : "Confirm Delete"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Breadcrumb: Home → Collections / [Collection] */}
      <nav
        aria-label="Breadcrumb"
        data-testid="cd-breadcrumb"
        className="flex items-center gap-1.5 flex-wrap text-xs mb-6"
      >
        <Link
          to="/"
          data-testid="cd-breadcrumb-home"
          className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] transition-colors duration-fast"
        >
          Home
        </Link>
        <span className="vp-muted">→</span>
        <Link
          to="/collections"
          data-testid="cd-breadcrumb-collections"
          className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] transition-colors duration-fast"
        >
          Collections
        </Link>
        <span className="vp-muted">/</span>
        <span
          data-testid="cd-breadcrumb-current"
          className="text-xs font-medium truncate max-w-[40ch]"
          title={collection?.name || ""}
        >
          {collection?.name || "…"}
        </span>
      </nav>

      {loading ? (
        <div className="vp-card p-8 vp-skeleton h-32 mb-6" />
      ) : !collection ? (
        <EmptyState Icon={Layers} title="Collection not found" body="It may have been deleted." actionLabel="Back to Collections" actionTo="/collections" />
      ) : (
        <>
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div className="min-w-0 flex-1">
              {editing ? (
                <div className="space-y-3 max-w-xl" data-testid="cd-edit-form">
                  <div>
                    <label htmlFor="cd-edit-name" className="block text-xs font-medium mb-1.5 vp-muted">
                      Name <span className="text-[hsl(var(--loss))]">*</span>
                    </label>
                    <input
                      id="cd-edit-name"
                      data-testid="cd-edit-name"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="vp-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="cd-edit-desc" className="block text-xs font-medium mb-1.5 vp-muted">Description</label>
                    <input
                      id="cd-edit-desc"
                      data-testid="cd-edit-description"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="A short description (optional)"
                      className="vp-input"
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={saveEdit}
                      disabled={savingEdit || !editName.trim()}
                      data-testid="cd-edit-save"
                      className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-50"
                    >
                      {savingEdit ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                      {savingEdit ? "Saving…" : "Save"}
                    </button>
                    <button
                      onClick={cancelEdit}
                      disabled={savingEdit}
                      data-testid="cd-edit-cancel"
                      className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
                    >
                      <X size={14} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="cd-title">{collection.name}</h1>
                  <p className="text-sm vp-muted mt-2" data-testid="cd-subtitle">Collection overview</p>
                  {collection.description ? (
                    <p className="text-sm mt-2" data-testid="cd-description">{collection.description}</p>
                  ) : null}
                  {/* === Task D: Collection Overview Summary Block ===
                      Three-count snapshot (Items / Categories / Value) placed
                      directly under the header. Uses the SAME helpers as the
                      existing OVERVIEW KPIs (kpi.totalValue via itemValue,
                      categories filtered by collection_id) — no new pricing
                      logic, no new endpoints. Updates automatically because
                      kpi is memoized off `items` and the category count is
                      derived inline. */}
                  {(() => {
                    const myCatCount = categories.filter((c) => c.collection_id === id).length;
                    return (
                      <div
                        data-testid="cd-summary-block"
                        className="mt-4 grid grid-cols-3 gap-4"
                      >
                        <div
                          data-testid="cd-summary-items"
                          className="vp-card p-3 text-center w-full min-w-0 overflow-hidden"
                        >
                          <div className="text-[10px] uppercase tracking-wider vp-muted truncate">Total Items</div>
                          <div className="text-xl sm:text-2xl font-semibold tracking-tight mt-1 truncate">{items.length}</div>
                        </div>
                        <div
                          data-testid="cd-summary-categories"
                          className="vp-card p-3 text-center w-full min-w-0 overflow-hidden"
                        >
                          <div className="text-[10px] uppercase tracking-wider vp-muted truncate">Total Categories</div>
                          <div className="text-xl sm:text-2xl font-semibold tracking-tight mt-1 truncate">{myCatCount}</div>
                        </div>
                        <div
                          data-testid="cd-summary-value"
                          className="vp-card p-3 text-center w-full min-w-0 overflow-hidden"
                        >
                          <div className="text-[10px] uppercase tracking-wider vp-muted truncate">Total Value</div>
                          <div className="text-xl sm:text-2xl font-semibold tracking-tight mt-1 font-mono truncate" title={money(kpi.totalValue)}>{money(kpi.totalValue)}</div>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Categories belonging to this collection. Each chip is a
                      clickable link to the Items list filtered by that
                      category via the existing URL-backed filter system. */}
                  {(() => {
                    const myCats = categories.filter((c) => c.collection_id === id);
                    if (myCats.length === 0) return null;
                    return (
                      <div
                        data-testid="cd-categories"
                        className="mt-3 flex flex-wrap gap-1.5"
                      >
                        {myCats.map((c) => (
                          <Link
                            key={c.id}
                            to={`/items?collection_id=${id}&category_id=${c.id}`}
                            data-testid={`cd-category-link-${c.id}`}
                            title={`View ${c.category_name} items`}
                            className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
                          >
                            <Tag size={12} />
                            <span className="truncate max-w-[180px]">{c.category_name}</span>
                          </Link>
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {!editing && (
                <button onClick={startEdit} data-testid="cd-edit" className="vp-btn vp-btn-secondary">
                  <Edit2 size={14} /> Edit
                </button>
              )}
              {!editing && (() => {
                const _tid = (collection?.type_id || "").toLowerCase();
                const _isBev = ["wine", "whisky", "spirits"].includes(_tid);
                const _myCatCount = categories.filter((c) => c.collection_id === id).length;
                if (_isBev && _myCatCount >= 1) return null;
                return (
                  <button
                    type="button"
                    onClick={() => setShowWineCatModal(true)}
                    data-testid="cd-create-category"
                    title="Create a new category attached to this collection"
                    className="vp-btn vp-btn-secondary"
                  >
                    <Tag size={14} /> Create Category
                  </button>
                );
              })()}
              <CollectionDataMenu
                onExportCsv={exportCollectionCsv}
                disabled={items.length === 0}
                itemCount={items.length}
                canCsvExport={canCsvExport}
              />
              {categories.filter((c) => c.collection_id === id).length > 0 && (
                <Link to={`/items/new?collection_id=${id}`} data-testid="cd-add-item" className="vp-btn vp-btn-primary">
                  <Plus size={16} /> Add Item
                </Link>
              )}
              {confirmDeleteColl ? (
                <div
                  className="flex items-center gap-1 bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-md p-1 shadow-sm"
                  data-testid="cd-delete-confirm"
                >
                  <button
                    type="button"
                    onClick={deleteCollection}
                    disabled={deletingColl}
                    data-testid="cd-delete-yes"
                    aria-label="Confirm delete collection"
                    className="vp-btn vp-btn-sm !h-9 !px-3 bg-[hsl(var(--loss))] text-white hover:opacity-90 disabled:opacity-50"
                  >
                    {deletingColl ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    <span className="text-xs font-medium">Delete</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteColl(false)}
                    disabled={deletingColl}
                    data-testid="cd-delete-cancel"
                    aria-label="Cancel delete"
                    className="vp-btn vp-btn-ghost vp-btn-sm !h-9 !w-9 !p-0 border border-[hsl(var(--border))]"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDeleteColl(true)}
                  data-testid="cd-delete"
                  className="vp-btn vp-btn-secondary border-[hsl(var(--loss))] text-[hsl(var(--loss))]"
                >
                  <Trash2 size={14} /> Delete Collection
                </button>
              )}
            </div>
          </div>

          {/* === Onboarding / empty-state banners ===
              Sits between the header and the KPI tiles so the user sees
              the relevant next action FIRST. Three states:
                - categories = 0          → "Create your first category"
                                            (no Add Item prompts shown)
                - categories ≥ 1, items=0 → "Add your first item" CTA
                - items > 0               → no banner (normal list view)
          */}
          {categories.filter((c) => c.collection_id === id).length === 0 ? (
            <EmptyState
              Icon={Tag}
              title="This collection has no categories yet"
              body="Create your first category to begin."
              actionLabel="Create Category"
              actionTo={`/collections/${id}/setup`}
              testid="cd-empty-no-cats"
            />
          ) : items.length === 0 ? (
            <EmptyState
              Icon={Layers}
              title="This category is empty"
              body="Add your first item."
              actionLabel="Add Item"
              actionTo={`/items/new?collection_id=${id}`}
              testid="cd-empty"
            />
          ) : null}

          {/* === KPI tiles ===
              Only render the value/KPI grid once the collection has items.
              A grid of zeros next to a "create your first category" prompt
              is visual noise we want to avoid during onboarding. */}
          {items.length > 0 && (
            <>
              <SectionHeader label="Overview" description="Key metrics at a glance" />
              <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-10" data-testid="cd-kpi">
                <Kpi label="Total Items"           value={kpi.count}            Icon={Boxes} testid="cd-kpi-count"
                  description="Number of distinct item records in this collection (each bottle line counts once, regardless of quantity)." />
                <Kpi label="Total Bottles"         value={Math.trunc(kpi.totalBottles)} Icon={Boxes} testid="cd-kpi-bottles"
                  description="Sum of current on-hand bottle quantities across every item in this collection." />
                <Kpi label="Total Cost"            value={money(kpi.totalCost)} Icon={Wallet} testid="cd-kpi-cost"
                  description="Sum of purchase costs paid across every item in this collection (inc GST where recorded)." />
                <Kpi
                  label={kpi.valuedCount === 0 ? "Awaiting Valuation" : "Collection Value"}
                  value={kpi.valuedCount === 0 ? "—" : money(kpi.totalValue)}
                  accent={kpi.valuedCount === 0 ? undefined : "gold"}
                  Icon={Coins}
                  testid="cd-kpi-value"
                  description={kpi.valuedCount === 0
                    ? "No items in this collection carry a Manual, Market, or recent-sale valuation yet — Collection Value will populate once at least one item is valued."
                    : "Current estimated value of the bottles you still own in this collection (ex GST). Uses Manual → Market → most recent sale → buy price per item."}
                />
                <Kpi label="Avg Bottle Value"      value={money(kpi.avgValue)}  Icon={Award} testid="cd-kpi-avg"
                  description="Collection Value ÷ remaining bottles — the average worth of one bottle you still own." />
                <Kpi label="Total Sales Value (Ex GST)" value={money(salesSummary.saleValueEx)} Icon={Wallet} testid="cd-kpi-total-sales-value"
                  description="Gross sale proceeds ex GST across every recorded sale from this collection." />
                <Kpi
                  label={kpi.valuedCount === 0
                    ? "Awaiting Valuation"
                    : (kpi.pl >= 0 ? "Unrealised Gain" : "Unrealised Loss")}
                  value={kpi.valuedCount === 0 ? "—" : money(Math.abs(kpi.pl))}
                  accent={kpi.valuedCount === 0 ? undefined : (kpi.pl >= 0 ? "profit" : "loss")}
                  Icon={kpi.valuedCount === 0 ? Coins : (kpi.pl >= 0 ? TrendingUp : TrendingDown)}
                  testid="cd-kpi-pl"
                  description={kpi.valuedCount === 0
                    ? "Gain/Loss will appear once at least one item in this collection carries a Manual, Market, or recent-sale valuation."
                    : "Collection Value minus purchase cost of the bottles you still own — the paper gain/loss you'd realise if you sold today."}
                />
                <Kpi
                  label={kpi.valuedCount === 0 ? "Awaiting Valuation" : "P/L %"}
                  value={kpi.valuedCount === 0 ? "—" : `${kpi.pct >= 0 ? "+" : ""}${fmt2(kpi.pct)}%`}
                  accent={kpi.valuedCount === 0 ? undefined : (kpi.pct > 0 ? "profit" : kpi.pct < 0 ? "loss" : "")}
                  Icon={Percent}
                  testid="cd-kpi-pct"
                  description={kpi.valuedCount === 0
                    ? "P/L % will appear once at least one item in this collection carries a Manual, Market, or recent-sale valuation."
                    : "Total P/L (realised + unrealised) as a percentage of total cost basis."}
                />
              </section>

              {/* Realised sales KPIs — spec-driven, mirrors the block on
                  Portfolio Analytics. Only shown when there's at least
                  one recorded sale to avoid an empty grid on new
                  collections. */}
              {sales.length > 0 && (
                <>
                  <SectionHeader label="Realised Performance" description="Sale totals and realised profit from recorded sales" />
                  <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-10" data-testid="cd-realised-kpi">
                    <Kpi label="Realised Profit (ex GST)" value={money(salesSummary.realisedEx)}
                      accent={salesSummary.realisedEx >= 0 ? "profit" : "loss"}
                      Icon={salesSummary.realisedEx >= 0 ? TrendingUp : TrendingDown}
                      testid="cd-kpi-realised-ex"
                      description="Actual profit realised on bottles you've already sold (ex GST): Σ (sale price ex GST − cost basis) × quantity sold." />
                    <Kpi label="Realised Profit (inc GST)" value={money(salesSummary.realisedInc)} Icon={Coins} testid="cd-kpi-realised-inc"
                      description="Same as Realised Profit but with GST included on sale proceeds and cost basis." />
                    <Kpi label="Total Quantity Sold" value={Math.trunc(salesSummary.qtySold)} Icon={Boxes} testid="cd-kpi-qty-sold"
                      description="Sum of bottles moved out via recorded sales in this collection." />
                    <Kpi label="Total Sale Value (ex GST)" value={money(salesSummary.saleValueEx)} Icon={Wallet} testid="cd-kpi-sale-value"
                      description="Gross sale proceeds ex GST across every recorded sale from this collection." />
                    <Kpi label="Remaining Quantity Value" value={money(kpi.totalValue)} Icon={Wallet} testid="cd-kpi-remaining-value"
                      description="Current estimated value of the bottles you still own after all recorded sales." />
                    <Kpi label={combinedPerformance >= 0 ? "Total Performance" : "Total Loss"}
                      value={money(combinedPerformance)}
                      accent={combinedPerformance >= 0 ? "profit" : "loss"}
                      Icon={combinedPerformance >= 0 ? TrendingUp : TrendingDown}
                      testid="cd-kpi-total-performance"
                      description="Realised profit + unrealised gain — the full picture of how this collection has performed to date." />
                  </section>
                </>
              )}
            </>
          )}

          {items.length === 0 ? null : (
            <>
              {/* === Items list — shown first so users can browse before charts === */}
              <SectionHeader label="Items" description={`${items.length} item${items.length === 1 ? "" : "s"} in this collection`} />
              {/* Mobile sort dropdown — the column-header sort UI below is
                  `hidden sm:grid`, so phones had no sort affordance. Exposes
                  the full sort matrix (name / cost / value / profit × asc/desc). */}
              <div className="sm:hidden mb-3 flex items-center gap-2" data-testid="cd-sort-mobile">
                <label htmlFor="cd-sort-mobile-select" className="text-xs vp-muted shrink-0">Sort by</label>
                <select
                  id="cd-sort-mobile-select"
                  data-testid="cd-sort-mobile-select"
                  value={`${sortKey}:${sortDir}`}
                  onChange={(e) => {
                    const [k, d] = e.target.value.split(":");
                    setSortKey(k);
                    setSortDir(d);
                  }}
                  className="vp-input !py-1.5 !text-sm flex-1 min-w-[16rem]"
                >
                  <option value="name:asc">Name (A → Z)</option>
                  <option value="name:desc">Name (Z → A)</option>
                  <option value="cost:desc">Purchase Price (high → low)</option>
                  <option value="cost:asc">Purchase Price (low → high)</option>
                  <option value="value:desc">Est. Value (high → low)</option>
                  <option value="value:asc">Est. Value (low → high)</option>
                  <option value="profit:desc">Profit (high → low)</option>
                  <option value="profit:asc">Profit (low → high)</option>
                </select>
              </div>
              <div className="vp-card overflow-hidden mb-10" data-testid="cd-items-list">
                <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]">
                  <div className="col-span-1 vp-label">Image</div>
                  <SortHeader testid="cd-sort-name"   col="col-span-3" align="left"  active={sortKey === "name"}   dir={sortDir} onClick={() => toggleSort("name")}>Name</SortHeader>
                  <SortHeader testid="cd-sort-cost"   col="col-span-2" align="right" active={sortKey === "cost"}   dir={sortDir} onClick={() => toggleSort("cost")}>Purchase Price</SortHeader>
                  <div className="col-span-1 vp-label text-right">Bottles</div>
                  <SortHeader testid="cd-sort-value"  col="col-span-2" align="right" active={sortKey === "value"}  dir={sortDir} onClick={() => toggleSort("value")}>Est. Value</SortHeader>
                  <SortHeader testid="cd-sort-profit" col="col-span-2" align="right" active={sortKey === "profit"} dir={sortDir} onClick={() => toggleSort("profit")}>Profit</SortHeader>
                  <div className="col-span-1 vp-label text-right">Actions</div>
                </div>
                {sortedItems.map((it) => {
                  const c = itemCost(it);
                  // 2026-02-26 — row value / profit now derive from the
                  // canonical `_val(it)` (backed by
                  // `computeItemValuation` with the item's sales
                  // history). Unvalued items (`includeInValuationTotals
                  // === false`) render "Awaiting Valuation" instead of
                  // a fabricated $0. Cost stays unconditional so the
                  // Purchase Price column always shows real spend.
                  const val = _val(it);
                  const isValued = val.includeInValuationTotals;
                  const v = isValued ? (val.current_unit_value * (val.remaining_qty || 1)) : 0;
                  const pl = isValued ? val.total_PL : 0;
                  return (
                    <div key={it.id} className="vp-table-row grid grid-cols-12 gap-4 items-center px-4 sm:px-6 py-4 border-b border-[hsl(var(--border))] last:border-b-0" data-testid={`cd-row-${it.id}`}>
                      <div className="col-span-2 sm:col-span-1">
                        <Link to={`/items/${it.id}`} state={{ from: location.pathname + location.search }} data-testid={`cd-row-thumb-${it.id}`} className="block w-12 h-12 bg-[hsl(var(--muted))] border border-[hsl(var(--border))] overflow-hidden rounded-md">
                          {(it.image || it.image_url) && <img src={it.image || it.image_url} alt="" className="w-full h-full object-cover" />}
                        </Link>
                      </div>
                      <div className="col-span-10 sm:col-span-3 min-w-0">
                        <Link to={`/items/${it.id}`} state={{ from: location.pathname + location.search }} data-testid={`cd-row-name-${it.id}`} className="text-base font-medium truncate hover:underline block">
                          {(it.item_data && it.item_data["Item Name"]) || it.name || it.item_name || "Untitled"}
                        </Link>
                        {/* Phase B display improvement: mirror the
                            ItemDetail subtitle here so users see the
                            full identity (whisky-style block on whisky
                            collections, "<Item Name> <Bottle Size>"
                            elsewhere) without opening each row. Hidden
                            when the composer returns the legacy
                            "Item details" fallback — that string is
                            redundant in a table context. */}
                        {(() => {
                          const desc = composeItemDescription(it, collection);
                          if (!desc || desc === "Item details") return null;
                          return (
                            <div
                              data-testid={`cd-row-description-${it.id}`}
                              title={desc}
                              className="mt-0.5 text-xs vp-muted truncate"
                            >
                              {desc}
                            </div>
                          );
                        })()}
                        <div className="mt-1">
                          {it.category_id ? (
                            <Link
                              to={`/items?category_id=${it.category_id}`}
                              data-testid={`cd-row-category-link-${it.id}`}
                              title="View all items in this category"
                              className="inline-block hover:opacity-80 transition-opacity duration-fast"
                            >
                              <CategoryPill category={catObj(it.category_id)} muted />
                            </Link>
                          ) : (
                            <CategoryPill category={catObj(it.category_id)} muted />
                          )}
                        </div>
                      </div>
                      <div className="col-span-12 sm:col-span-2 flex items-center justify-between sm:justify-end gap-2 sm:block sm:text-right text-sm font-mono" data-testid={`cd-row-cost-${it.id}`}>
                        <span className="sm:hidden vp-muted text-xs">Purchase Price:</span>
                        <span className="inline-flex items-baseline gap-1.5 min-w-0">
                          <span className="truncate">{money(c)}</span>
                          <CurrencyBadge code={it.currency_code} testid={`cd-row-currency-cost-${it.id}`} />
                        </span>
                      </div>
                      {/* Total Bottles — integer count via itemQty(). Legacy
                          items with no quantity render as "0" so the column
                          matches the rest of the qty-weighted analytics.
                          Display-only; sort options intentionally unchanged. */}
                      <div className="col-span-12 sm:col-span-1 flex items-center justify-between sm:justify-end gap-2 sm:block sm:text-right text-sm font-mono" data-testid={`cd-row-bottles-${it.id}`}>
                        <span className="sm:hidden vp-muted text-xs">Bottles:</span>
                        <span className="truncate">{Math.trunc(itemQty(it))}</span>
                      </div>
                      <div className="col-span-12 sm:col-span-2 flex items-center justify-between sm:justify-end gap-2 sm:block sm:text-right text-sm font-mono" data-testid={`cd-row-value-${it.id}`}>
                        <span className="sm:hidden vp-muted text-xs">Estimated Value:</span>
                        {isValued ? (
                          <InferredValueIndicator inferred={false}>
                            <span className="inline-flex items-baseline gap-1.5 min-w-0">
                              <span className="truncate">{money(v)}</span>
                              <CurrencyBadge code={it.currency_code} testid={`cd-row-currency-value-${it.id}`} />
                            </span>
                          </InferredValueIndicator>
                        ) : (
                          <span className="vp-muted italic truncate" data-testid={`cd-row-awaiting-${it.id}`}>Awaiting Valuation</span>
                        )}
                      </div>
                      <div
                        className={`col-span-12 sm:col-span-2 flex items-center justify-between sm:justify-end gap-2 sm:block sm:text-right text-sm font-mono font-medium ${
                          !isValued ? "vp-muted" : pl > 0 ? "text-[hsl(var(--profit))]" : pl < 0 ? "text-[hsl(var(--loss))]" : "vp-muted"
                        }`}
                        data-testid={`cd-row-profit-${it.id}`}
                      >
                        <span className="sm:hidden vp-muted text-xs font-sans font-normal">Profit:</span>
                        {isValued ? (
                          <span className="inline-flex items-baseline gap-1.5 min-w-0">
                            <span className="truncate">{money(pl)}</span>
                            <CurrencyBadge code={it.currency_code} testid={`cd-row-currency-profit-${it.id}`} />
                          </span>
                        ) : (
                          <span className="italic truncate" data-testid={`cd-row-profit-awaiting-${it.id}`}>Awaiting Valuation</span>
                        )}
                      </div>
                      <div className="col-span-12 sm:col-span-1 flex sm:justify-end gap-1">
                        {confirmDeleteItemId === it.id ? (
                          <>
                            <button
                              type="button"
                              onClick={() => deleteItem(it.id)}
                              disabled={deletingItemId === it.id}
                              data-testid={`cd-row-delete-yes-${it.id}`}
                              aria-label="Confirm delete item"
                              className="vp-btn vp-btn-sm !h-8 !px-2 bg-[hsl(var(--loss))] text-white hover:opacity-90 disabled:opacity-50"
                            >
                              {deletingItemId === it.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                              <span className="text-xs font-medium">Delete</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteItemId(null)}
                              disabled={deletingItemId === it.id}
                              data-testid={`cd-row-delete-cancel-${it.id}`}
                              aria-label="Cancel delete"
                              className="vp-btn vp-btn-ghost vp-btn-sm !h-8 !w-8 !p-0 border border-[hsl(var(--border))]"
                            >
                              <X size={13} />
                            </button>
                          </>
                        ) : (
                          <>
                            <Link to={`/items/${it.id}/edit`} state={{ from: location.pathname + location.search }} data-testid={`cd-row-edit-${it.id}`} title="Edit item" aria-label="Edit item" className="vp-btn vp-btn-ghost vp-btn-sm !h-8 !w-8 !p-0 border border-[hsl(var(--border))]">
                              <Edit2 size={13} />
                            </Link>
                            <button
                              onClick={() => setConfirmDeleteItemId(it.id)}
                              data-testid={`cd-row-delete-${it.id}`}
                              title="Delete item"
                              aria-label="Delete item"
                              className="vp-btn vp-btn-ghost vp-btn-sm !h-8 !w-8 !p-0 border border-[hsl(var(--loss))] text-[hsl(var(--loss))]"
                            >
                              <Trash2 size={13} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* === Charts: category breakdown === */}
              <SectionHeader label="Analytics" description="Category and time-based breakdowns" />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                <ChartFrame title="Value by Category" subtitle="Estimated value distribution" testid="cd-chart-value-pie">
                  {valuePieData.length === 0 ? <ChartEmpty message="No estimated values yet." /> : <CategoryPieChart data={valuePieData} />}
                </ChartFrame>
                <ChartFrame title="Cost vs Estimated Value by Category" subtitle="Side-by-side comparison" testid="cd-chart-cost-value">
                  <CategoryBarChart data={costValueBarData} />
                </ChartFrame>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                <ChartFrame title="Profit by Category" subtitle="Estimated value − cost" testid="cd-chart-profit">
                  <CategoryBarChart data={profitBarData} />
                </ChartFrame>
                <ChartFrame title="Value Over Time" subtitle="Cumulative estimated value" testid="cd-chart-value-time">
                  <ValueLineChart data={timeSeries} dataKey="value" label="Cumulative value" />
                </ChartFrame>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-10">
                <ChartFrame title="Profit Trend Over Time" subtitle="Cumulative P/L" testid="cd-chart-profit-time">
                  <ProfitLineChart data={timeSeries} />
                </ChartFrame>
                {insights && (
                  <div className="vp-card p-6 vp-fade-in" data-testid="cd-insights">
                    <h3 className="text-lg font-medium tracking-tight mb-1">Item Insights</h3>
                    <p className="text-xs vp-muted mb-4">Notable items in this collection</p>
                    <div className="space-y-3">
                      <Insight Icon={Award}        label="Highest Value"  item={insights.highestValue}  display={insights.highestValue ? money(_val(insights.highestValue).current_value) : "—"} />
                      <Insight Icon={TrendingUp}   label="Highest Profit" item={insights.highestProfitItem} display={money(insights.highestProfitDisplay)} accent="profit" />
                      <Insight Icon={TrendingDown} label="Biggest Loss"   item={insights.biggestLossItem}   display={money(insights.biggestLossDisplay)}   accent={insights.biggestLossDisplay < 0 ? "loss" : ""} />
                      <Insight Icon={Coins}        label="Average Value"  display={money(insights.avgValue)} />
                    </div>
                  </div>
                )}
              </div>

              {/* Wine analytics: rendered only on Wine collections that have data. */}
              {isWineCol && (byRegion.length + byProducer.length + byGrape.length + byWineCategory.length) > 0 && (
                <>
                  <SectionHeader label="Wine Analytics" description="Breakdowns by region, producer, grape and wine category" />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-10">
                    {byWineCategory.length > 0 && (
                      <RollupTable
                        title="Category Totals"
                        subtitle={`Grouped by item.Category (${byWineCategory.length} ${byWineCategory.length === 1 ? "value" : "values"})`}
                        rows={byWineCategory}
                        money={money}
                        testid="cd-rollup-wine-category"
                      />
                    )}
                    {byRegion.length > 0 && (
                      <RollupTable
                        title="Region Totals"
                        subtitle={`${byRegion.length} ${byRegion.length === 1 ? "region" : "regions"}`}
                        rows={byRegion}
                        money={money}
                        testid="cd-rollup-region"
                      />
                    )}
                    {byProducer.length > 0 && (
                      <RollupTable
                        title="Producer Totals"
                        subtitle={`${byProducer.length} ${byProducer.length === 1 ? "producer" : "producers"}`}
                        rows={byProducer}
                        money={money}
                        testid="cd-rollup-producer"
                      />
                    )}
                    {byGrape.length > 0 && (
                      <RollupTable
                        title="Grape Totals"
                        subtitle={`${byGrape.length} ${byGrape.length === 1 ? "grape" : "grapes"}`}
                        rows={byGrape}
                        money={money}
                        testid="cd-rollup-grape"
                      />
                    )}
                  </div>
                </>
              )}

              {/* === Spirits analytics: per-item-data-field rollups ===
                  Only renders on Spirits collections. Dimensions are
                  Spirits-specific (Distillery / Type / Cask Type / Country)
                  so no wine-labelled data ever appears here. */}
              {isSpiritsCol && (byDistillery.length + bySpiritType.length + byCaskType.length + byCountry.length) > 0 && (
                <>
                  <SectionHeader label="Spirits Analytics" description="Breakdowns by distillery, type, cask type and country" />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-10">
                    {byDistillery.length > 0 && (
                      <RollupTable
                        title="Distillery Totals"
                        subtitle={`${byDistillery.length} ${byDistillery.length === 1 ? "distillery" : "distilleries"}`}
                        rows={byDistillery}
                        money={money}
                        testid="cd-rollup-distillery"
                      />
                    )}
                    {bySpiritType.length > 0 && (
                      <RollupTable
                        title="Type Totals"
                        subtitle={`${bySpiritType.length} ${bySpiritType.length === 1 ? "type" : "types"}`}
                        rows={bySpiritType}
                        money={money}
                        testid="cd-rollup-spirit-type"
                      />
                    )}
                    {byCaskType.length > 0 && (
                      <RollupTable
                        title="Cask Type Totals"
                        subtitle={`${byCaskType.length} ${byCaskType.length === 1 ? "cask type" : "cask types"}`}
                        rows={byCaskType}
                        money={money}
                        testid="cd-rollup-cask-type"
                      />
                    )}
                    {byCountry.length > 0 && (
                      <RollupTable
                        title="Country Totals"
                        subtitle={`${byCountry.length} ${byCountry.length === 1 ? "country" : "countries"}`}
                        rows={byCountry}
                        money={money}
                        testid="cd-rollup-country"
                      />
                    )}
                  </div>
                </>
              )}

              {/* === Items list moved above the charts; keep this comment so the
                  fragment closes cleanly. === */}

              {/* Sales History — every sale recorded against items in this
                  collection. Rendered after the items list so users hit
                  their inventory first. */}
              <SectionHeader label="Sales History" description="Every sale recorded against items in this collection" />
              <section className="mb-10" data-testid="cd-sales-history">
                <SalesHistoryTable sales={sales} testid="cd-sales-history-table" />
              </section>
            </>
          )}
        </>
      )}

      <BottomNav current="collections" />

      <WineCategoryGuidedModal
        open={showWineCatModal}
        onClose={() => setShowWineCatModal(false)}
        onSaved={() => refreshCategories()}
        collectionId={id}
        collectionName={collection?.name || ""}
        collectionType={collection?.type_id || ""}
      />

    </div>
  );
}

/* ============================================================ */
function Kpi({ label, value, Icon, accent, testid, description }) {
  return (
    <div
      data-testid={testid}
      title={description || (typeof value === "string" ? `${label}: ${value}` : label)}
      aria-label={description ? `${label} — ${description}` : label}
      className="vp-card p-4 min-w-0 overflow-hidden"
    >
      <div className="flex items-center gap-2 mb-2 min-w-0">
        {Icon && (
          <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
            accent === "gold" ? "bg-[hsl(var(--accent))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
            : accent === "profit" ? "bg-[hsl(var(--profit))]/15 text-[hsl(var(--profit))]"
            : accent === "loss" ? "bg-[hsl(var(--loss))]/15 text-[hsl(var(--loss))]"
            : "bg-[hsl(var(--muted))] vp-muted"
          }`}>
            <Icon size={13} />
          </div>
        )}
        <div className="vp-label !text-[10px] truncate">{label}</div>
      </div>
      <div
        className={`font-mono text-xl font-semibold tracking-tight min-w-0 flex-shrink truncate overflow-hidden text-ellipsis ${
        accent === "gold" ? "text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
        : accent === "profit" ? "text-[hsl(var(--profit))]"
        : accent === "loss" ? "text-[hsl(var(--loss))]"
        : ""
      }`}
      >{value}</div>
    </div>
  );
}

function Insight({ Icon, label, item, display, accent }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-[hsl(var(--border))] last:border-b-0">
      <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
        accent === "profit" ? "bg-[hsl(var(--profit))]/15 text-[hsl(var(--profit))]"
        : accent === "loss" ? "bg-[hsl(var(--loss))]/15 text-[hsl(var(--loss))]"
        : "bg-[hsl(var(--accent))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
      }`}>
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="vp-label !text-[10px]">{label}</div>
        {item ? (
          // Phase 10 (2026-02) — insight items pointing at deleted /
          // orphan sales render as plain text (no <Link>) so users
          // don't hit a 404 on a ghost item. `item.orphan === true`
          // is set by the insights resolver when the sale's item is
          // no longer in the live inventory.
          item.orphan ? (
            <div
              className="text-sm font-medium truncate"
              title="Item has been deleted — sale preserved from the ledger"
              data-testid="cd-insight-orphan-name"
            >
              {item.name || item.item_name || "Untitled"}
            </div>
          ) : (
            <Link to={`/items/${item.id}`} state={{ from: location.pathname + location.search }} className="text-sm font-medium hover:underline truncate block">{item.name || item.item_name || "Untitled"}</Link>
          )
        ) : (
          <div className="text-sm vp-muted">—</div>
        )}
      </div>
      <div className={`font-mono text-sm font-semibold shrink-0 ${
        accent === "profit" ? "text-[hsl(var(--profit))]"
        : accent === "loss" ? "text-[hsl(var(--loss))]"
        : ""
      }`}>{display}</div>
    </div>
  );
}

/**
 * CollectionDataMenu — small dropdown beside "Add Item" that exposes the
 * "Export Collection (CSV)" action.  Mirrors the data-testids used by
 * Items.jsx so the same automation can drive both screens.
 */
function CollectionDataMenu({ onExportCsv, disabled, itemCount, canCsvExport }) {
  const [open, setOpen] = useState(false);
  // Phase 8 gate — Basic tier or above required to see the CSV button.
  if (!canCsvExport) return null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        disabled={disabled}
        data-testid="cd-data-menu"
        title="Export this collection"
        className="vp-btn vp-btn-secondary disabled:opacity-50"
      >
        <Download size={14} /> Data
        <ChevronDown size={12} className={`transition-transform duration-fast ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="cd-data-menu-list"
          role="menu"
          className="absolute right-0 top-full mt-1 w-56 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elevated))] shadow-md z-20 overflow-hidden"
        >
          <button
            type="button"
            role="menuitem"
            onClick={(e) => { e.preventDefault(); setOpen(false); onExportCsv(); }}
            data-testid="cd-export-collection-csv"
            className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--muted))] flex items-center gap-2"
          >
            <Download size={13} className="vp-muted" />
            <span>
              <span className="block">Export Collection (CSV)</span>
              <span className="block text-[10px] vp-muted">{itemCount} row{itemCount === 1 ? "" : "s"}</span>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}


/**
 * RollupTable — generic per-dimension table used by the Wine Analytics block.
 * Renders Items / Cost / Value / P/L for each row, plus a footer total.
 * Pure presentation; data is pre-aggregated in the parent.
 */
function RollupTable({ title, subtitle, rows, money, testid }) {
  const totals = rows.reduce(
    (acc, r) => ({
      count: acc.count + r.count,
      cost: acc.cost + r.total_cost,
      value: acc.value + r.total_est_value,
      profit: acc.profit + r.total_profit,
    }),
    { count: 0, cost: 0, value: 0, profit: 0 }
  );
  return (
    <div className="vp-card p-5" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-base font-medium tracking-tight">{title}</h3>
        <span className="text-[10px] uppercase tracking-wider vp-muted">{subtitle}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              <th className="py-2 pr-3 text-[10px] uppercase tracking-wider font-medium vp-muted">Name</th>
              <th className="py-2 px-2 text-right text-[10px] uppercase tracking-wider font-medium vp-muted">Items</th>
              <th className="py-2 px-2 text-right text-[10px] uppercase tracking-wider font-medium vp-muted">Cost</th>
              <th className="py-2 px-2 text-right text-[10px] uppercase tracking-wider font-medium vp-muted">Value</th>
              <th className="py-2 pl-2 text-right text-[10px] uppercase tracking-wider font-medium vp-muted">P/L</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-t border-[hsl(var(--border))]" data-testid={`${testid}-row-${r.name}`}>
                <td className="py-2 pr-3 truncate max-w-[18ch]" title={r.name}>{r.name}</td>
                <td className="py-2 px-2 text-right font-mono">{r.count}</td>
                <td className="py-2 px-2 text-right font-mono">{money(r.total_cost)}</td>
                <td className="py-2 px-2 text-right font-mono text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]">{money(r.total_est_value)}</td>
                <td className={`py-2 pl-2 text-right font-mono ${r.total_profit > 0 ? "text-[hsl(var(--profit))]" : r.total_profit < 0 ? "text-[hsl(var(--loss))]" : "vp-muted"}`}>
                  {money(r.total_profit)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[hsl(var(--border-strong))] font-medium">
              <td className="pt-2.5 pr-3 vp-label">Totals</td>
              <td className="pt-2.5 px-2 text-right font-mono">{totals.count}</td>
              <td className="pt-2.5 px-2 text-right font-mono">{money(totals.cost)}</td>
              <td className="pt-2.5 px-2 text-right font-mono">{money(totals.value)}</td>
              <td className={`pt-2.5 pl-2 text-right font-mono ${totals.profit > 0 ? "text-[hsl(var(--profit))]" : totals.profit < 0 ? "text-[hsl(var(--loss))]" : "vp-muted"}`}>{money(totals.profit)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}


/**
 * SortHeader — clickable column header for the Items table.
 *  - `align` toggles left vs right label alignment.
 *  - `active` highlights the column and renders an up/down arrow.
 *  - Keyboard-accessible (it is a real <button>).
 */
function SortHeader({ children, col, align = "left", active, dir, onClick, testid }) {
  const justify = align === "right" ? "justify-end" : "justify-start";
  return (
    <div className={`${col} vp-label ${align === "right" ? "text-right" : ""}`}>
      <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        aria-label={`Sort by ${children}${active ? ` (currently ${dir})` : ""}`}
        aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
        className={`inline-flex items-center gap-1 ${justify} cursor-pointer hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] transition-colors duration-fast focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[hsl(var(--gold))] rounded-sm ${active ? "text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" : ""}`}
      >
        <span>{children}</span>
        {active ? (
          dir === "asc"
            ? <ArrowUp size={10} strokeWidth={2.25} />
            : <ArrowDown size={10} strokeWidth={2.25} />
        ) : (
          <span className="inline-block w-2.5" aria-hidden />
        )}
      </button>
    </div>
  );
}

